import { Alert, Link } from '@mm/components';
import * as Widgets from '@mm/constants/widgets';
import { DraggableProvided } from 'react-beautiful-dnd';
import { ButtonType } from '@mm/state/model/ButtonModel';
import { ChatbotChannel } from '@mm/state/model/ChatbotModel';
import WidgetModel, {
  TagsWidgetConfig,
  WidgetBase,
  WidgetTypes,
} from '@mm/state/model/WidgetModel';
import {
  activeFacebookPagePlanInfoSelector,
  activeFacebookPageSelector,
} from '@mm/state/selectors/facebookPageSelector';
import { hasDeactivatedNumberSelector } from '@mm/state/selectors/twillio';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AttachmentWidget } from '../../../../../../common/widgets/AttachmentWidget/AttachmentWidget';
import { AttributeWidget } from '../../../../../../common/widgets/AttributeWidget/AttributeWidget';
import { EmailWidget } from '../../../../../../common/widgets/EmailWidget/EmailWidget';
import { FormWidget } from '../../../../../../common/widgets/FormWidget/Form';
import { GalleryWidget } from '../../../../../../common/widgets/GalleryWidget/GalleryWidget';
import { GifWidget } from '../../../../../../common/widgets/GifWidget/GifWidget';
import { ImageWidget } from '../../../../../../common/widgets/ImageWidget/ImageWidget';
import { NavigateToDialogueWidget } from '../../../../../../common/widgets/NavigateToDialogueWidget/NavigateToDialogueWidget';
import { OptInWidget } from '../../../../../../common/widgets/OptInWidget/OptInWidget';
import { OtnWidget } from '../../../../../../common/widgets/OtnWidget/OtnWidget';
import { QuestionWidget } from '../../../../../../common/widgets/QuestionWidget/QuestionWidget';
import { SendDataWidget } from '../../../../../../common/widgets/SendData/SendData';
import { TagsWidget } from '../../../../../../common/widgets/TagsWidget/TagsWidget';
import { TextWidget } from '../../../../../../common/widgets/TextWidget/TextWidget';
import { TypingWidget } from '../../../../../../common/widgets/TypingWidget/TypingWidget';
import { VideoWidget } from '../../../../../../common/widgets/VideoWidget/VideoWidget';
import { AudioWidget } from '../../../../../../common/widgets/AudioWidget/AudioWidget';
import { DelayWidget } from '../../../../../../common/widgets/DelayWidget/DelayWidget';
import './WidgetByType.pcss';
import { WidgetLayout } from '../../../../../../common/widgets/common/WidgetLayout/WidgetLayout';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { landmineNames } from '@mm/constants/landmine-names';

export type WidgetByTypeProps = {
  widget: WidgetModel;
  chatbotId: number;
  disableDragging?: boolean;
  disableDeleting?: boolean;
  disableConditionalLogic?: boolean;
  forAds?: boolean;
  disabledButtonTypes?: ButtonType[];
  restrictToChannels?: ChatbotChannel[];
  draggableProvided: DraggableProvided;
  isSmsRelated?: boolean;
  smsOnly?: boolean;
  platforms?: PlatformType[];
  showAttributePicker?: boolean;
  showAttributesStartWarning?: boolean;
  firstWidgetNotAllowedWarning?: WidgetAlerts;
  showOnlyNameAttributes?: boolean;
  dialogueId: number;
  onFocus?: (widgetId?: number) => void;
  isFocused?: boolean;
  isDragging?: boolean;
  forInstagram?: boolean;
  showCountByChannel?: boolean;
  widgetsToExclude?: WidgetTypes[];
  usedInSlideout?: boolean;
  pageWidgets: WidgetModel[];
};

// TODO deprecate
export type WidgetContainerRefProp = {};

export type WidgetAlerts = {
  showWarning: boolean;
  message: string;
  startbotType: 'instagram_cg' | 'facebook_cg' | null;
};

// TODO change consumer imports
export const ErrorPortal = WidgetLayout.ErrorPortal;

// tslint:disable-next-line:cyclomatic-complexity
export const WidgetByType = (props: WidgetByTypeProps) => {
  const {
    widget,
    forAds,
    disabledButtonTypes,
    chatbotId,
    disableDragging,
    disableDeleting,
    disableConditionalLogic,
    restrictToChannels,
    draggableProvided,
    isSmsRelated,
    smsOnly,
    showAttributePicker = true,
    showOnlyNameAttributes = false,
    dialogueId,
    onFocus = () => {},
    isFocused,
    isDragging,
    platforms,
    forInstagram,
    widgetsToExclude,
    showCountByChannel,
    showAttributesStartWarning = false,
    firstWidgetNotAllowedWarning = {
      showWarning: false,
      message: '',
      startbotType: null,
    },
    usedInSlideout,
    pageWidgets,
  } = props;

  let widgetToRender: JSX.Element | null = null;
  const facebookPage = useSelector(activeFacebookPageSelector);

  const [alertInformation, setAlertInformation] = useState<{
    alertText: string;
    includeEUPolicyLink: boolean;
    needsUpgrade?: boolean;
  }>();

  const [alertHidden, setAlertHidden] = useState(false);

  const css = useMemo(() => {
    return {
      container: `widget-by-type__${widget.type}`,
      widget: `widget-by-type__widget`,
    };
  }, [widget.type]);

  const { openUniversalUpgradeLandmineModal } = useModalActions();

  useEffect(() => {
    switch (widget.type) {
      case Widgets.TEXT:
      case WidgetTypes.GALLERY:
        if (!facebookPage?.euLimitsAt) break;
        if (widget.type === Widgets.TEXT && !widget.buttons.length) break;
        setAlertInformation({
          alertText:
            'On Messenger this widget will only show up on Android & iOS devices due to',
          includeEUPolicyLink: true,
        });
        break;
      case Widgets.ATTACHMENT:
      case WidgetTypes.VIDEO:
        if (!facebookPage?.euLimitsAt) break;
        setAlertInformation({
          alertText:
            'This widget will be sent as a URL link on Messenger due to',
          includeEUPolicyLink: true,
        });
        break;
      case WidgetTypes.OTN:
        if (!facebookPage?.euLimitsAt) break;
        setAlertInformation({
          alertText: 'This widget is no longer valid on Messenger due to',
          includeEUPolicyLink: true,
        });
        break;
      case WidgetTypes.TYPING:
        if (facebookPage?.euLimitsAt) {
          setAlertInformation({
            alertText: 'This widget is no longer valid on Messenger due to',
            includeEUPolicyLink: true,
          });
        }
        if (forInstagram) {
          setAlertInformation({
            alertText:
              'This typing widget will introduce a delay before the next message, but no typing animation will show up on Instagram.',
            includeEUPolicyLink: false,
          });
        }
        break;
      case WidgetTypes.GIF:
        if (!forInstagram) break;
        setAlertInformation({
          alertText:
            'IMPORTANT: GIFs are not fully supported on Instagram. Your Instagram contacts will only see a a static version of this GIF.',
          includeEUPolicyLink: false,
        });
        break;
      case WidgetTypes.EMAIL:
        if (!notAvailable) break;
        setAlertInformation({
          alertText: ' You need to upgrade to use this feature ',
          includeEUPolicyLink: false,
          needsUpgrade: true,
        });
        break;
      default:
    }
  }, [facebookPage, widget.type, widget.buttons, forInstagram]);
  const activeFacebookPagePlanInfo = useSelector(
    activeFacebookPagePlanInfoSelector,
  ).info;

  const isFree = activeFacebookPagePlanInfo.isFreeOrSpecialPlan;
  const notAvailable = widget.type === WidgetTypes.EMAIL && isFree;

  const hasDeactivatedNumber = useSelector(hasDeactivatedNumberSelector);
  const shouldShowCountByChannel =
    (isSmsRelated && !hasDeactivatedNumber) || showCountByChannel;

  const commentGuardFirstUnsupportedWidgets = [
    Widgets.IMAGE,
    Widgets.TEXT,
    Widgets.ATTACHMENT,
    Widgets.AUDIO,
    Widgets.GALLERY,
    Widgets.GIF,
    Widgets.VIDEO,
    Widgets.OPT_IN,
    Widgets.OTN,
    Widgets.TYPING,
    firstWidgetNotAllowedWarning.startbotType === 'facebook_cg'
      ? Widgets.NAVIGATE
      : null,
  ];

  if (widget.type === WidgetTypes.SEND_DATA) {
    widgetToRender = (
      <SendDataWidget
        widget={widget}
        draggableProvided={draggableProvided}
        usedInSlideout={usedInSlideout}
        platforms={platforms}
      />
    );
  }

  if (
    [
      Widgets.QUICK_QUESTION,
      WidgetTypes.SMS_OPT_IN,
      WidgetTypes.SMS_OPT_IN_V2,
      WidgetTypes.SMS_OPT_IN_REPLY,
    ].includes(widget.type)
  ) {
    widgetToRender = (
      <QuestionWidget
        forAds={forAds}
        forInstagram={forInstagram}
        chatbotId={chatbotId}
        widget={widget}
        disableDragging={disableDragging}
        disableDeleting={disableDeleting}
        disableConditionalLogic={disableConditionalLogic}
        restrictToChannels={restrictToChannels}
        draggableProvided={draggableProvided}
        isSmsRelated={isSmsRelated}
        showAttributePicker={showAttributePicker}
        showAttributesStartWarning={showAttributesStartWarning}
        showOnlyNameAttributes={showOnlyNameAttributes}
        dialogueId={dialogueId}
        widgetsToExclude={widgetsToExclude}
        showCountByChannel={shouldShowCountByChannel}
        usedInSlideout={usedInSlideout}
        pageWidgets={pageWidgets}
        platforms={platforms}
      />
    );
  }

  if (widget.type === Widgets.IMAGE) {
    widgetToRender = (
      <ImageWidget
        widget={widget}
        isSmsRelated={isSmsRelated}
        smsOnly={smsOnly}
        draggableProvided={draggableProvided}
        platforms={platforms}
      />
    );
  }

  if (widget.type === Widgets.FORM) {
    widgetToRender = (
      <FormWidget
        widget={widget}
        draggableProvided={draggableProvided}
        showAttributePicker={showAttributePicker}
        showOnlyNameAttributes={showOnlyNameAttributes}
        restrictToChannels={restrictToChannels}
        isSmsRelated={isSmsRelated}
        showCountByChannel={shouldShowCountByChannel}
        usedInSlideout={usedInSlideout}
        pageWidgets={pageWidgets}
        platforms={platforms}
      />
    );
  }

  if (widget.type === Widgets.TEXT) {
    widgetToRender = (
      <TextWidget
        disabledButtonTypes={disabledButtonTypes}
        chatbotId={chatbotId}
        widget={widget}
        forAds={forAds}
        disableDragging={disableDragging}
        disableDeleting={disableDeleting}
        disableConditionalLogic={disableConditionalLogic}
        draggableProvided={draggableProvided}
        isSmsRelated={isSmsRelated}
        smsOnly={smsOnly}
        showAttributePicker={showAttributePicker}
        showAttributesStartWarning={showAttributesStartWarning}
        showOnlyNameAttributes={showOnlyNameAttributes}
        forInstagram={forInstagram}
        showCountByChannel={shouldShowCountByChannel}
        usedInSlideout={usedInSlideout}
        widgetsToExclude={widgetsToExclude}
        pageWidgets={pageWidgets}
        platforms={platforms}
      />
    );
  }

  if (widget.type === Widgets.EMAIL) {
    widgetToRender = (
      <EmailWidget
        widget={widget}
        isExpanded={true}
        draggableProvided={draggableProvided}
        platforms={platforms}
      />
    );
  }

  if (widget.type === Widgets.ATTACHMENT) {
    widgetToRender = (
      <AttachmentWidget
        widget={widget}
        smsOnly={smsOnly}
        isSmsRelated={isSmsRelated}
        forInstagram={forInstagram}
        draggableProvided={draggableProvided}
        platforms={platforms}
      />
    );
  }

  if (widget.type === Widgets.AUDIO) {
    widgetToRender = (
      <AudioWidget
        widget={widget}
        smsOnly={smsOnly}
        isSmsRelated={isSmsRelated}
        forInstagram={forInstagram}
        draggableProvided={draggableProvided}
        platforms={platforms}
      />
    );
  }

  if (widget.type === WidgetTypes.ATTRIBUTE) {
    widgetToRender = (
      <AttributeWidget
        widget={widget}
        draggableProvided={draggableProvided}
        platforms={platforms}
      />
    );
  }

  if (widget.type === WidgetTypes.GIF) {
    widgetToRender = (
      <GifWidget
        widget={widget}
        isSmsRelated={isSmsRelated}
        smsOnly={smsOnly}
        draggableProvided={draggableProvided}
        platforms={platforms}
      />
    );
  }

  if (widget.type === WidgetTypes.NAVIGATE) {
    widgetToRender = (
      <NavigateToDialogueWidget
        widget={widget}
        draggableProvided={draggableProvided}
        isSmsRelated={isSmsRelated}
        firstWidgetNotAllowedWarning={
          firstWidgetNotAllowedWarning.startbotType === 'instagram_cg'
            ? firstWidgetNotAllowedWarning
            : undefined
        }
        widgetsToExclude={widgetsToExclude}
        platforms={platforms}
        usedInSlideout={usedInSlideout}
      />
    );
  }

  if (widget.type === WidgetTypes.TYPING) {
    widgetToRender = (
      <TypingWidget
        widget={widget}
        smsOnly={smsOnly}
        draggableProvided={draggableProvided}
        isSmsRelated={isSmsRelated}
        platforms={platforms}
      />
    );
  }

  if (widget.type === WidgetTypes.TAGS) {
    // Note: our current widget type system is not sophisticated enough to figure it out correctly
    widgetToRender = (
      <TagsWidget
        widget={widget as unknown as WidgetBase<TagsWidgetConfig>}
        draggableProvided={draggableProvided}
        platforms={platforms}
      />
    );
  }

  if (widget.type === WidgetTypes.VIDEO) {
    widgetToRender = (
      <VideoWidget
        isSmsRelated={isSmsRelated}
        forInstagram={forInstagram}
        smsOnly={smsOnly}
        widget={widget}
        draggableProvided={draggableProvided}
        platforms={platforms}
      />
    );
  }

  if (widget.type === WidgetTypes.OPT_IN) {
    widgetToRender = (
      <OptInWidget
        widget={widget}
        draggableProvided={draggableProvided}
        showAttributePicker={showAttributePicker}
        showAttributesStartWarning={showAttributesStartWarning}
        showCountByChannel={shouldShowCountByChannel}
        forInstagram={forInstagram}
        widgetsToExclude={widgetsToExclude}
        platforms={platforms}
        usedInSlideout={usedInSlideout}
      />
    );
  }

  if (widget.type === WidgetTypes.OTN) {
    widgetToRender = (
      <OtnWidget
        widget={widget}
        draggableProvided={draggableProvided}
        isSmsRelated={isSmsRelated}
        forInstagram={forInstagram}
        smsOnly={smsOnly}
        showAttributePicker={showAttributePicker}
        platforms={platforms}
        usedInSlideout={usedInSlideout}
      />
    );
  }

  if (widget.type === WidgetTypes.GALLERY) {
    const disabledButtonTypesMapped = [
      forInstagram && ButtonType.CALL,
      ...(disabledButtonTypes || []),
    ].filter(Boolean);

    widgetToRender = (
      <GalleryWidget
        disabledButtonTypes={disabledButtonTypesMapped as ButtonType[]}
        widget={widget}
        smsOnly={smsOnly}
        draggableProvided={draggableProvided}
        isSmsRelated={isSmsRelated}
        forInstagram={forInstagram}
        showAttributePicker={showAttributePicker}
        usedInSlideout={usedInSlideout}
        widgetsToExclude={widgetsToExclude}
        platforms={platforms}
      />
    );
  }

  if (widget.type === Widgets.DELAY) {
    widgetToRender = (
      <DelayWidget
        widget={widget}
        draggableProvided={draggableProvided}
        platforms={platforms}
      />
    );
  }

  // TODO consider abstracting alerts from this render
  if (widgetToRender) {
    return (
      <WidgetLayout.Container
        {...props}
        onFocus={
          notAvailable
            ? () =>
                openUniversalUpgradeLandmineModal({
                  landmineName: landmineNames.NOTIFICATION_WIDGET_EMAIL,
                })
            : onFocus
        }
        css={css}
        invalid={notAvailable}
        widget={widget}
      >
        {widgetToRender}

        {!alertHidden &&
          alertInformation &&
          !firstWidgetNotAllowedWarning.showWarning && (
            <ErrorPortal widget={widget}>
              <Alert
                type="warning"
                className="widget__warning-alert"
                showHeader
                closeIconProps={{
                  onClick: () => {
                    setAlertHidden(true);
                    setAlertInformation(undefined);
                  },
                }}
              >
                {alertInformation.alertText}
                {alertInformation.includeEUPolicyLink && (
                  <Link
                    className="widget__warning-link"
                    href="https://customers.ai/help/article/cg1qvuexfy-updated-messaging-policies-for-eu-pages-users"
                    isBlank
                  >
                    {' '}
                    EU policy changes.
                  </Link>
                )}
                {alertInformation.needsUpgrade && (
                  <a
                    href="#"
                    onClick={() =>
                      openUniversalUpgradeLandmineModal({
                        landmineName: landmineNames.NOTIFICATION_WIDGET_EMAIL,
                      })
                    }
                  >
                    Open Upgrade Modal
                  </a>
                )}
              </Alert>
            </ErrorPortal>
          )}

        {firstWidgetNotAllowedWarning.showWarning &&
          commentGuardFirstUnsupportedWidgets.map(w => {
            if (w === widget.type) {
              return (
                <ErrorPortal widget={widget}>
                  <div className="widget__error-alert-wrapper">
                    <Alert
                      className="widget__error-alert"
                      type={'error'}
                      showCloseIcon={false}
                    >
                      {firstWidgetNotAllowedWarning.message}
                    </Alert>
                  </div>
                </ErrorPortal>
              );
            }
            return null;
          })}
      </WidgetLayout.Container>
    );
  }

  return widgetToRender;
};
